import { createRoot } from 'react-dom/client';
import { App } from './App.js';
import { SessionProvider } from './app/session.js';
import { ErrorBoundary } from './components/ErrorBoundary.js';
import { ToastProvider } from './components/Toast.js';
import { whenIdle } from './lib/lazy.js';
import { initPrefs } from './lib/preferences.js';
import { installScrollRestore } from './lib/scrollRestore.js';
import { keepPageStylesFirst } from './lib/styleOrder.js';
import { registerServiceWorker } from './offline/registerSw.js';
// UI typeface (OFL, bundled — no font CDN). The canvas uses the licensed font catalog instead.
import '@fontsource-variable/noto-sans-thai/wght.css';
import './styles/tokens.css';
import './styles/base.css';
import './styles/app.css';
import './styles/editor.css';
import './styles/mobile.css';
import './styles/m1.css';
import './styles/present.css';
// Catalog screens: styles/catalog.css travels with the catalog pages; these few rules stay global.
import './styles/catalog-global.css';
// TOR screens: styles/tor.css travels with the TOR pages; these few rules stay global (see the file).
import './styles/tor-global.css';
import './styles/shell.css';
import './styles/platform.css';
import './styles/organize.css';
import './styles/folders.css';
import './chat/chat.css';
// The app shell's "ของฉัน" pieces (the Ctrl+K button in the header, the files strip): a GLOBAL sheet.
// It adjusts shell and phone rules at equal specificity, so it has to come after them — it used to get
// there only because index.html listed the `session` chunk's sheet after this one by mistake
// (build/delivery.ts entryStylesheetLast).
import './styles/myfiles.css';
import './styles/xforma.css';

// Pages are separate chunks: their stylesheets must still come before the global ones (lib/styleOrder.ts).
keepPageStylesFirst();

// Theme / contrast / text size on <html> before the first render (no flash of the wrong theme).
initPrefs();
// A new page starts at the top; Back returns to where the list was left (lib/scrollRestore.ts).
installScrollRestore();

// A build may carry a stamp (VITE_XC_BUILD_STAMP): the update-flow test builds the same sources twice
// and needs the two releases to have different file names, like two real releases do.
const buildStamp: unknown = import.meta.env.VITE_XC_BUILD_STAMP;
if (typeof buildStamp === 'string' && buildStamp) document.documentElement.dataset.xcBuild = buildStamp;

// No <StrictMode>: the editor owns side-effectful singletons per mount (lease acquisition,
// single-use magic-link verification) that must not run twice in development.
createRoot(document.getElementById('root')!).render(
  <ToastProvider>
    <SessionProvider>
      <ErrorBoundary>
        <App />
      </ErrorBoundary>
    </SessionProvider>
  </ToastProvider>,
);

// App-shell service worker (production builds only; never caches /v1/ API responses).
registerServiceWorker();

// Speed as this device sees it (perf/collector.ts): a chunk of its own, fetched once the page has
// loaded and the browser is idle, so the first paint pays nothing for it.
window.addEventListener('load', () => whenIdle(() => void import('./perf/collector.js').then((m) => m.startPerfCollector()).catch(() => undefined), 8000), { once: true });
